import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { useIncidents } from 'context/IncidentsContext';
import { useBehaviorPlans } from 'context/BehaviorPlansContext';
import { useStudentTeams } from 'context/StudentTeamsContext';
import {
  useUserProfiles,
  shortRoleLabel,
  roleLabelFor,
} from 'context/UserProfilesContext';
import { deriveNameFromEmail } from 'lib/currentUser';
import { humanizeSupabaseError } from '../../lib/supabaseErrors';

// ---------------------------------------------------------------------------
// PrintStudentSummary — a one-page-per-student, print-first summary sheet.
//
// This is the artifact a teacher hands to a parent at an IEP meeting or
// walks a district administrator through: everything you need to know
// about the student on one sheet, in ink-friendly typography, with no
// chrome from the app itself. Chrome buttons ("Print", "Back to profile")
// are visible on screen and hidden by `@media print` styles so a browser
// "Save as PDF" also produces a clean document.
//
// Reads from the same four contexts as the rest of the app, so what a
// parent sees on paper is guaranteed to match what the team sees in the
// dashboard. No cached rollups, no synthetic examples.
//
// Route: /print/student/:studentId
// ---------------------------------------------------------------------------

const safeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const calcAge = (dob) => {
  const d = safeDate(dob);
  if (!d) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
};

const humanize = (raw) => {
  if (!raw) return '';
  return String(raw).replace(/[-_]/g, ' ');
};

const iepReviewStatus = (dueRaw) => {
  const due = safeDate(dueRaw);
  if (!due) return null;
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const days = Math.round((due - startOfToday) / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: `Overdue by ${Math.abs(days)} days`, tone: 'danger' };
  if (days === 0) return { label: 'Due today', tone: 'danger' };
  if (days <= 30) return { label: `Due in ${days} days`, tone: 'warning' };
  if (days <= 90) return { label: `Due in ${days} days`, tone: 'info' };
  return { label: due.toLocaleDateString(), tone: 'neutral' };
};

const goalStatusLabel = (raw) => {
  switch (raw) {
    case 'in_progress':
      return 'In progress';
    case 'mastered':
      return 'Mastered';
    case 'discontinued':
      return 'Discontinued';
    case 'on_hold':
      return 'On hold';
    case 'not_started':
      return 'Not started';
    default:
      return humanize(raw) || 'In progress';
  }
};

const PrintStudentSummary = () => {
  const { studentId: urlId } = useParams();
  const navigate = useNavigate();
  const decodedId = urlId ? decodeURIComponent(urlId) : null;

  const { students, loading: studentsLoading, error: studentsError } =
    useStudents();
  const { goals, listAllProgressEntries } = useIEPGoals();
  const { incidents } = useIncidents();
  const { activePlanForStudent } = useBehaviorPlans();
  const { teamForStudent } = useStudentTeams();
  const { getUserById } = useUserProfiles();

  const student = useMemo(
    () =>
      decodedId
        ? (Array.isArray(students) ? students : []).find(
            (s) => s.id === decodedId,
          )
        : null,
    [students, decodedId],
  );

  // Latest progress entry per active goal is what a parent cares about.
  // We load in a wide window (last 12 months) so a September meeting can
  // still see June data.
  const [entriesByGoal, setEntriesByGoal] = useState(new Map());
  const [entriesError, setEntriesError] = useState(null);

  useEffect(() => {
    if (!student) return undefined;
    const now = new Date();
    const yearAgo = new Date(now);
    yearAgo.setFullYear(now.getFullYear() - 1);
    const from = yearAgo.toISOString().slice(0, 10);
    const to = now.toISOString().slice(0, 10);
    let cancelled = false;
    listAllProgressEntries({ from, to })
      .then((rows) => {
        if (cancelled) return;
        const map = new Map();
        for (const e of rows || []) {
          const arr = map.get(e.goalId) || [];
          arr.push(e);
          map.set(e.goalId, arr);
        }
        for (const [k, arr] of map.entries()) {
          arr.sort((a, b) =>
            String(b.observedOn || '').localeCompare(String(a.observedOn || '')),
          );
          map.set(k, arr);
        }
        setEntriesByGoal(map);
      })
      .catch((err) => {
        if (!cancelled) {
          setEntriesError(
            humanizeSupabaseError(err, 'Progress entries could not load.'),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [student, listAllProgressEntries]);

  const studentGoals = useMemo(
    () =>
      (Array.isArray(goals) ? goals : [])
        .filter((g) => g.studentId === decodedId)
        .sort((a, b) => {
          const rank = (s) =>
            s === 'in_progress' ? 0 : s === 'on_hold' ? 1 : s === 'mastered' ? 2 : 3;
          return rank(a.status) - rank(b.status);
        }),
    [goals, decodedId],
  );

  const studentIncidents = useMemo(
    () =>
      (Array.isArray(incidents) ? incidents : [])
        .filter((i) => i.studentId === decodedId)
        .sort((a, b) =>
          String(b.occurredAt || '').localeCompare(String(a.occurredAt || '')),
        )
        .slice(0, 10),
    [incidents, decodedId],
  );

  const activePlan = useMemo(
    () => (decodedId ? activePlanForStudent(decodedId) : null),
    [activePlanForStudent, decodedId],
  );

  // "Team on this student" — the people who share access to log data
  // on this student, enriched with their user_profiles rows for
  // rendering the name + role pill on the printed sheet.
  const teamRows = useMemo(() => {
    if (!decodedId) return [];
    return teamForStudent(decodedId)
      .map((m) => ({
        member: m,
        profile: getUserById(m.userId),
      }))
      .sort((a, b) => {
        const an = (a.profile?.displayName ||
          deriveNameFromEmail(a.profile?.email) ||
          'Zzz').toLowerCase();
        const bn = (b.profile?.displayName ||
          deriveNameFromEmail(b.profile?.email) ||
          'Zzz').toLowerCase();
        return an.localeCompare(bn);
      });
  }, [decodedId, teamForStudent, getUserById]);

  // Split "Accommodations & Modifications" into a list. Storage is a
  // single text column separated by newlines (see 0010 comment on
  // students.accommodations), so we render as bullets when the teacher
  // used newlines, otherwise as one paragraph.
  const accommodationsList = useMemo(() => {
    const raw = student?.accommodations || '';
    if (!raw.trim()) return [];
    return raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }, [student?.accommodations]);

  if (studentsLoading && !student) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-gray-500">
        <div className="text-center">
          <Icon
            name="Loader2"
            size={24}
            className="animate-spin mx-auto mb-2"
          />
          Loading student summary…
        </div>
      </div>
    );
  }

  if (studentsError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-md text-center">
          <Icon
            name="AlertCircle"
            size={28}
            className="mx-auto mb-2 text-red-600"
          />
          <p className="font-medium text-gray-900 mb-1">
            Couldn't load student data
          </p>
          <p className="text-sm text-gray-600 mb-4">{studentsError}</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-md border border-gray-300 text-sm hover:bg-gray-50"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-md text-center">
          <Icon
            name="UserX"
            size={28}
            className="mx-auto mb-2 text-gray-500"
          />
          <p className="font-medium text-gray-900 mb-1">
            No student found for this ID
          </p>
          <p className="text-sm text-gray-600 mb-4">
            Student ID <span className="font-mono">{decodedId}</span> isn't in
            your roster. It may have been archived or deleted.
          </p>
          <button
            type="button"
            onClick={() => navigate('/student-profile-management')}
            className="px-4 py-2 rounded-md border border-gray-300 text-sm hover:bg-gray-50"
          >
            Back to roster
          </button>
        </div>
      </div>
    );
  }

  const displayName =
    `${student.firstName || ''} ${student.lastName || ''}`.trim() ||
    'Unnamed student';
  const age = calcAge(student.dateOfBirth);
  const initials =
    `${(student.firstName?.[0] || '?').toUpperCase()}${(student.lastName?.[0] || '').toUpperCase()}`;
  const reviewStatus = iepReviewStatus(student.iepAnnualReviewDue);
  const generatedAt = new Date();

  return (
    <div className="print-summary min-h-screen bg-gray-100 print:bg-white">
      {/* Screen-only action bar */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-[8.5in] mx-auto flex items-center justify-between p-3 gap-2">
          <button
            type="button"
            onClick={() =>
              navigate(
                `/student-profile-management/${encodeURIComponent(student.id)}`,
              )
            }
            className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
          >
            <Icon name="ChevronLeft" size={16} />
            Back to profile
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/student-profile-management')}
              className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50"
            >
              <Icon name="Users" size={14} />
              Roster
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-md bg-primary text-white hover:bg-primary/90"
            >
              <Icon name="Printer" size={14} />
              Print / Save as PDF
            </button>
          </div>
        </div>
      </div>

      {/* Printable sheet */}
      <div className="max-w-[8.5in] mx-auto bg-white p-8 print:p-0 print:max-w-none">
        {/* Header */}
        <header className="border-b border-gray-300 pb-4 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg font-semibold border border-primary/20 flex-shrink-0">
                {initials}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                  {displayName}
                </h1>
                <div className="text-sm text-gray-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span className="font-mono text-xs">{student.id}</span>
                  {student.grade && <span>Grade {student.grade}</span>}
                  {safeDate(student.dateOfBirth) && (
                    <span>
                      DOB {safeDate(student.dateOfBirth).toLocaleDateString()}
                      {age !== null && <> · Age {age}</>}
                    </span>
                  )}
                </div>
                {student.primaryDisability && (
                  <p className="text-sm text-gray-700 mt-1">
                    <span className="text-gray-500">Primary disability:</span>{' '}
                    <span className="capitalize">
                      {humanize(student.primaryDisability)}
                    </span>
                  </p>
                )}
              </div>
            </div>
            <div className="text-right text-xs text-gray-500 flex-shrink-0">
              <div className="font-semibold text-gray-700">
                EduTracker Pro
              </div>
              <div>Student Summary</div>
              <div className="mt-1">
                Generated {generatedAt.toLocaleDateString()}{' '}
                {generatedAt.toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          </div>
        </header>

        {/* Top row: IEP timeline + emergency contact */}
        <section className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <SectionHeading icon="CalendarClock" title="IEP timeline" />
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  Start date
                </div>
                <div className="font-medium text-gray-900">
                  {safeDate(student.iepStartDate)
                    ? safeDate(student.iepStartDate).toLocaleDateString()
                    : 'Not set'}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  Annual review due
                </div>
                <div className="font-medium text-gray-900">
                  {safeDate(student.iepAnnualReviewDue)
                    ? safeDate(
                        student.iepAnnualReviewDue,
                      ).toLocaleDateString()
                    : 'Not set'}
                </div>
                {reviewStatus && (
                  <div
                    className={`inline-block mt-1 text-[11px] font-medium px-1.5 py-0.5 rounded border ${
                      reviewStatus.tone === 'danger'
                        ? 'bg-red-50 border-red-200 text-red-700'
                        : reviewStatus.tone === 'warning'
                        ? 'bg-amber-50 border-amber-200 text-amber-800'
                        : reviewStatus.tone === 'info'
                        ? 'bg-blue-50 border-blue-200 text-blue-700'
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                    }`}
                  >
                    {reviewStatus.label}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <SectionHeading icon="Phone" title="Emergency contact" />
            {student.emergencyContact &&
            typeof student.emergencyContact === 'object' &&
            (student.emergencyContact.name ||
              student.emergencyContact.phone ||
              student.emergencyContact.email) ? (
              <div className="text-sm space-y-1">
                {student.emergencyContact.name && (
                  <div>
                    <span className="text-gray-500">Name:</span>{' '}
                    <span className="font-medium text-gray-900">
                      {student.emergencyContact.name}
                    </span>
                    {student.emergencyContact.relationship && (
                      <span className="text-gray-500">
                        {' '}
                        ({student.emergencyContact.relationship})
                      </span>
                    )}
                  </div>
                )}
                {student.emergencyContact.phone && (
                  <div>
                    <span className="text-gray-500">Phone:</span>{' '}
                    <span className="font-medium text-gray-900">
                      {student.emergencyContact.phone}
                    </span>
                  </div>
                )}
                {student.emergencyContact.email && (
                  <div>
                    <span className="text-gray-500">Email:</span>{' '}
                    <span className="font-medium text-gray-900">
                      {student.emergencyContact.email}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                Not on file.
              </p>
            )}
          </div>
        </section>

        {/* Team on this student */}
        <section className="mb-6 print:break-inside-avoid">
          <SectionHeading
            icon="Users2"
            title={`Team on this student (${teamRows.length})`}
            subtitle="Everyone who logs data for this student"
          />
          {teamRows.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              No teammates assigned yet.
            </p>
          ) : (
            <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {teamRows.map(({ member, profile }) => {
                const name =
                  profile?.displayName ||
                  deriveNameFromEmail(profile?.email) ||
                  profile?.email ||
                  'Former teammate';
                const roleLabel =
                  profile?.discipline ||
                  roleLabelFor(profile?.role) ||
                  (member.roleOnTeam ? member.roleOnTeam : '');
                const short = shortRoleLabel(profile);
                return (
                  <li
                    key={member.id}
                    className="flex items-baseline gap-2"
                  >
                    <span className="font-medium text-gray-900">{name}</span>
                    {(roleLabel || short) && (
                      <span className="text-gray-500 text-xs">
                        {roleLabel || short}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Accommodations */}
        {accommodationsList.length > 0 && (
          <section className="mb-6 print:break-inside-avoid">
            <SectionHeading
              icon="ClipboardList"
              title="Accommodations & Modifications"
            />
            {accommodationsList.length === 1 ? (
              <p className="text-sm text-gray-800">{accommodationsList[0]}</p>
            ) : (
              <ul className="list-disc pl-5 text-sm text-gray-800 space-y-0.5">
                {accommodationsList.map((line, idx) => (
                  <li key={idx}>{line}</li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Support details — communication, medical, transportation, dietary.
            Compact section that only renders if the profile has at least
            one of these filled in. Parents ask about these regularly. */}
        {(student.communicationMethod ||
          student.medicalInfo ||
          student.transportationNeeds ||
          student.dietaryRestrictions) && (
          <section className="mb-6 print:break-inside-avoid">
            <SectionHeading icon="Info" title="Support details" />
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {student.communicationMethod && (
                <MiniField
                  label="Communication method"
                  value={humanize(student.communicationMethod)}
                />
              )}
              {student.transportationNeeds && (
                <MiniField
                  label="Transportation"
                  value={student.transportationNeeds}
                />
              )}
              {student.medicalInfo && (
                <div className="col-span-2">
                  <MiniField
                    label="Medical information"
                    value={student.medicalInfo}
                  />
                </div>
              )}
              {student.dietaryRestrictions && (
                <div className="col-span-2">
                  <MiniField
                    label="Dietary restrictions"
                    value={student.dietaryRestrictions}
                  />
                </div>
              )}
            </div>
          </section>
        )}

        {/* IEP Goals */}
        <section className="mb-6 print:break-inside-avoid">
          <SectionHeading
            icon="Target"
            title={`IEP Goals (${studentGoals.length})`}
          />
          {studentGoals.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              No goals recorded for this student yet.
            </p>
          ) : (
            <div className="space-y-3">
              {studentGoals.map((g) => {
                const latest = entriesByGoal.get(g.id)?.[0] || null;
                const targetDate = safeDate(g.targetDate);
                return (
                  <article
                    key={g.id}
                    className="border border-gray-200 rounded-md p-3 text-sm print:break-inside-avoid"
                  >
                    <div className="flex items-start justify-between gap-3 mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-500 capitalize">
                          {humanize(g.goalArea) || 'Goal'}
                        </span>
                        <span
                          className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                            g.status === 'mastered'
                              ? 'bg-green-50 border-green-200 text-green-700'
                              : g.status === 'discontinued'
                              ? 'bg-gray-100 border-gray-200 text-gray-500'
                              : 'bg-blue-50 border-blue-200 text-blue-700'
                          }`}
                        >
                          {goalStatusLabel(g.status)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 text-right flex-shrink-0">
                        {targetDate && (
                          <div>Target {targetDate.toLocaleDateString()}</div>
                        )}
                      </div>
                    </div>
                    {g.specific && (
                      <p className="text-gray-900 leading-snug mb-2 whitespace-pre-line">
                        {g.specific}
                      </p>
                    )}
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <MiniField
                        label="Baseline"
                        value={g.baseline}
                        empty="—"
                      />
                      <MiniField
                        label="Target"
                        value={g.target}
                        empty="—"
                      />
                      <MiniField
                        label="Mastery criteria"
                        value={g.masteryCriteria}
                        empty="—"
                      />
                    </div>

                    {/* Progress bar */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>Progress</span>
                        <span className="font-medium text-gray-900">
                          {Number.isFinite(Number(g.progressPercent))
                            ? `${Number(g.progressPercent)}%`
                            : '—'}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full bg-primary print:bg-black"
                          style={{
                            width: `${Math.max(
                              0,
                              Math.min(100, Number(g.progressPercent) || 0),
                            )}%`,
                          }}
                        />
                      </div>
                    </div>

                    {latest && (
                      <p className="text-xs text-gray-600 mt-2">
                        <span className="text-gray-500">Latest data:</span>{' '}
                        <span className="font-medium">
                          {latest.value}
                          {latest.unit ? ` ${latest.unit}` : ''}
                        </span>
                        {latest.targetValue != null && (
                          <> / {latest.targetValue} target</>
                        )}
                        {' '}
                        on{' '}
                        {new Date(
                          `${String(latest.observedOn).slice(0, 10)}T12:00:00`,
                        ).toLocaleDateString()}
                        {latest.supportLevel && (
                          <> · {humanize(latest.supportLevel)}</>
                        )}
                      </p>
                    )}
                  </article>
                );
              })}
            </div>
          )}
          {entriesError && (
            <p className="text-xs text-red-600 mt-2">{entriesError}</p>
          )}
        </section>

        {/* Behavior Support Plan */}
        {activePlan && (
          <section className="mb-6 print:break-inside-avoid">
            <SectionHeading
              icon="ShieldCheck"
              title="Behavior Support Plan"
              subtitle={`Tier ${activePlan.mtssTier || 2}`}
            />
            <div className="border border-gray-200 rounded-md p-3 text-sm space-y-2">
              {activePlan.targetBehaviorDefinition && (
                <MiniField
                  label="Target behavior (operational)"
                  value={activePlan.targetBehaviorDefinition}
                />
              )}
              {activePlan.hypothesizedFunction && (
                <MiniField
                  label="Hypothesized function"
                  value={humanize(activePlan.hypothesizedFunction)}
                />
              )}
              {activePlan.replacementBehavior && (
                <MiniField
                  label="Replacement behavior"
                  value={activePlan.replacementBehavior}
                />
              )}
              {activePlan.smartGoal && (
                <MiniField label="SMART goal" value={activePlan.smartGoal} />
              )}
              {Array.isArray(activePlan.antecedentInterventions) &&
                activePlan.antecedentInterventions.length > 0 && (
                  <MiniList
                    label="Antecedent interventions"
                    items={activePlan.antecedentInterventions}
                  />
                )}
              {Array.isArray(activePlan.teachingStrategies) &&
                activePlan.teachingStrategies.length > 0 && (
                  <MiniList
                    label="Teaching strategies"
                    items={activePlan.teachingStrategies}
                  />
                )}
              {Array.isArray(activePlan.consequenceInterventions) &&
                activePlan.consequenceInterventions.length > 0 && (
                  <MiniList
                    label="Consequence interventions"
                    items={activePlan.consequenceInterventions}
                  />
                )}
              {activePlan.escalationPlan && (
                <MiniField
                  label="Escalation plan"
                  value={activePlan.escalationPlan}
                />
              )}
            </div>
          </section>
        )}

        {/* Recent incidents */}
        <section className="mb-6 print:break-inside-avoid">
          <SectionHeading
            icon="AlertTriangle"
            title={`Recent behavior incidents (${studentIncidents.length})`}
            subtitle="Most recent 10"
          />
          {studentIncidents.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              No incidents on file.
            </p>
          ) : (
            <table className="w-full text-xs border border-gray-200">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-2 border-b border-gray-200 w-24">Date</th>
                  <th className="p-2 border-b border-gray-200 w-32">Type</th>
                  <th className="p-2 border-b border-gray-200 w-16">
                    Intensity
                  </th>
                  <th className="p-2 border-b border-gray-200">Description</th>
                </tr>
              </thead>
              <tbody>
                {studentIncidents.map((i) => (
                  <tr key={i.id} className="align-top">
                    <td className="p-2 border-b border-gray-100 whitespace-nowrap">
                      {new Date(i.occurredAt).toLocaleDateString()}
                    </td>
                    <td className="p-2 border-b border-gray-100 capitalize">
                      {humanize(i.behaviorType) || '—'}
                    </td>
                    <td className="p-2 border-b border-gray-100">
                      {i.intensity != null ? `${i.intensity}/5` : '—'}
                    </td>
                    <td className="p-2 border-b border-gray-100 text-gray-700">
                      {i.behaviorDescription || i.notes || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Notes */}
        {student.notes && (
          <section className="mb-6 print:break-inside-avoid">
            <SectionHeading icon="StickyNote" title="Teacher notes" />
            <p className="text-sm text-gray-800 whitespace-pre-line">
              {student.notes}
            </p>
          </section>
        )}

        {/* Confidentiality footer */}
        <footer className="border-t border-gray-300 mt-6 pt-3 text-[10px] text-gray-500 leading-relaxed">
          <p>
            <strong>Confidential.</strong> This summary contains protected
            education records under FERPA. Distribute only to the student's
            IEP team, parents/guardians, and school personnel with a
            legitimate educational interest. Please shred or securely
            delete when no longer needed.
          </p>
          <p className="mt-1">
            Generated by EduTracker Pro from live database on{' '}
            {generatedAt.toLocaleString()}.
          </p>
        </footer>
      </div>

      {/* Print styles: kill the gray page background + hide chrome */}
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0.5in;
          }
          body {
            background: #fff !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print-summary {
            background: #fff !important;
          }
        }
      `}</style>
    </div>
  );
};

const SectionHeading = ({ icon, title, subtitle }) => (
  <div className="flex items-baseline justify-between mb-2 print:break-after-avoid">
    <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 uppercase tracking-wide">
      <Icon name={icon} size={14} className="text-gray-500" />
      {title}
    </h2>
    {subtitle && (
      <span className="text-xs text-gray-500">{subtitle}</span>
    )}
  </div>
);

const MiniField = ({ label, value, empty }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-gray-500">
      {label}
    </div>
    <div className="text-sm text-gray-900 whitespace-pre-line">
      {value || empty || <span className="text-gray-400">—</span>}
    </div>
  </div>
);

const MiniList = ({ label, items }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">
      {label}
    </div>
    <ul className="list-disc pl-4 text-sm text-gray-900 space-y-0.5">
      {items.map((item, idx) => (
        <li key={idx}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>
      ))}
    </ul>
  </div>
);

export default PrintStudentSummary;
